import React, { useState, useEffect } from 'react';
import { differenceInMinutes } from 'date-fns';

import BookOneOffSession from 'coaching/components/CoachingHomeView/BookOneOffSession';
import BookSessionConfirmationSidebar from 'coaching/components/CoachingHomeView/BookSessionConfirmationSidebar';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachAvatarWithSubject from 'coaching/components/CoachAvatarWithSubject';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import SidebarHeader from 'coaching/components/SidebarHeader';
import Row from 'components/Row';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';
import GetPossibleCoachingSessionsForCoachContainer from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import { rescheduleCoachingSessionInstance } from 'coaching/actions/CoachingActions';
import useWeekOffset from 'coaching/hooks/useWeekOffset';
import {
  CoachMetadata,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { TimeslotFrequencyInput, CoachingEventDefinitionInput } from 'globalTypes';
import GET_COACHING_HOME_DASHBOARD_QUERY from 'coaching/queries/GetCoachingHomeDashboard.gql';
import { userReadableDateTimeFormatLocalTZ } from 'lib/dateTimeFormat';
import { SelfScheduleOptions } from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';

const userTimezone = TimezoneHelper.userTimezoneDefaultValue ?? 'America/Los_Angeles';

type RescheduleSteps = 'reschedule-session' | 'confirmation';

export type Props = {
  originalInstanceUuid: string;
  subjectName: string;
  sessionTools: CoachingSessionTools;
  participantUuid: string;
  coach: CoachMetadata;
  onClose: () => void;
  selfScheduleOptions: SelfScheduleOptions;
};

export default function RescheduleCoachingSessionInstanceSidebar({
  originalInstanceUuid,
  sessionTools,
  subjectName,
  coach,
  onClose,
  participantUuid,
  selfScheduleOptions,
}: Props) {
  const eventPrefix = `${coachingHomeEventPrefix}.reschedule_instance_sidebar`;
  function trackKey(key: string) {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  const [step, setStep] = useState<RescheduleSteps>('reschedule-session');
  const [eventDefinition, setEventDefinition] = useState<CoachingEventDefinitionInput>();
  const [submitting, setSubmitting] = useState(false);
  const { weekOffset, setWeekOffset, startDateTimeRange, endDateTimeRange } = useWeekOffset(6);

  useEffect(() => {
    // reset state if we change the participant/subject/coach/instance
    setStep('reschedule-session');
    setEventDefinition(undefined);
    setWeekOffset(0);
  }, [participantUuid, sessionTools.subject.uuid, coach.uuid, originalInstanceUuid]);

  if (!startDateTimeRange || !endDateTimeRange) {
    return (
      <ErrorReporter error="BookNewCoachingSessionSidebar - tried to render without a dateTimeRange">
        Oops! Looks like something went wrong. Please try refreshing the page.
      </ErrorReporter>
    );
  }

  async function handleSubmit() {
    if (eventDefinition) {
      setSubmitting(true);
      trackKey('update_session.click');
      await rescheduleCoachingSessionInstance(
        originalInstanceUuid,
        eventDefinition.startDateTime,
        eventDefinition.endDateTime,
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        () => setStep('confirmation')
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to book a session without selecting a date and time. Please try again.'
      );
    }
  }

  const stepToComponentMap: Record<RescheduleSteps, React.ReactElement> = {
    'reschedule-session': (
      <>
        <BoxBody size="xsmall">
          <CoachAvatarWithSubject subjectName={subjectName} coachFullName={coach.name} />
        </BoxBody>
        <Divider size="flush" />
        <GetPossibleCoachingSessionsForCoachContainer
          subjectUuid={sessionTools.subject.uuid}
          coachUuid={coach.uuid}
          startDateTimeRange={startDateTimeRange}
          endDateTimeRange={endDateTimeRange}
          frequency={TimeslotFrequencyInput.once}
          participantUuid={participantUuid}>
          {props => (
            <>
              <BookOneOffSession
                {...props}
                data-test="book-one-off-session"
                startDateTimeRange={startDateTimeRange}
                endDateTimeRange={endDateTimeRange}
                weekOffset={weekOffset}
                timezone={coach.timezone ?? userTimezone}
                sessionSlots={props.sessionSlots}
                onSelectReservation={res => {
                  setEventDefinition(res?.eventDefinition);
                }}
                selfScheduleOptions={selfScheduleOptions}
                onWeekForward={() => setWeekOffset(prev => prev + 1)}
                onWeekBack={() => setWeekOffset(prev => prev - 1)}
                eventPrefix={eventPrefix}
              />
              <RescheduleSessionInstanceFooter
                disabled={!eventDefinition}
                submitting={submitting}
                onSubmit={handleSubmit}
                eventDefinition={eventDefinition}
              />
            </>
          )}
        </GetPossibleCoachingSessionsForCoachContainer>
      </>
    ),
    confirmation: (
      <BookSessionConfirmationSidebar
        data-test="reschedule-session-confirmation-step"
        streamUuid={sessionTools.streamUuid}
        eventDefinition={eventDefinition}
        eventPrefix={`${eventPrefix}.update_session_confirmation`}
      />
    ),
  };

  return (
    <>
      <SidebarHeader title="Edit session" eventPrefix={eventPrefix} onClose={onClose} />
      {stepToComponentMap[step]}
    </>
  );
}

export function RescheduleSessionInstanceFooter({
  disabled,
  submitting,
  onSubmit,
  eventDefinition,
}: {
  disabled: boolean;
  submitting: boolean;
  onSubmit: () => Promise<void> | void;
  eventDefinition?: CoachingEventDefinitionInput;
}) {
  return (
    <SidebarFooter>
      <BoxBody size="small">
        <EventExplanation eventDefinition={eventDefinition} />
        <Button
          data-test="update-session-button"
          full
          disabled={disabled}
          loading={submitting}
          onClick={onSubmit}>
          Update session
        </Button>
      </BoxBody>
    </SidebarFooter>
  );
}

function EventExplanation({ eventDefinition }: { eventDefinition?: CoachingEventDefinitionInput }) {
  if (!eventDefinition) return null;
  const duration = differenceInMinutes(eventDefinition.endDateTime, eventDefinition.startDateTime);
  return (
    <Row size="small">
      Your session will be rescheduled for{' '}
      <Text weight="semibold">
        {userReadableDateTimeFormatLocalTZ(eventDefinition?.startDateTime)}
      </Text>
      . You'll be billed ${duration} after the session.
    </Row>
  );
}
